import * as THREE from "three";
import * as CANNON from "cannon";
import _ from "lodash";
import { createCanvas } from "canvas";

import {
  material_options,
  default_label_color,
  default_dice_color
} from "./settings";

export const create_shape = (vertices, faces, radius) => {
  const cv = vertices.map(
    v => new CANNON.Vec3(v.x * radius, v.y * radius, v.z * radius)
  );
  const cf = faces.map(f => _.dropRight(f, 1));
  return new CANNON.ConvexPolyhedron(cv, cf);
};

export const make_vectors_from_vertices = vertices =>
  vertices.map(v => new THREE.Vector3().fromArray(v).normalize());

export const chamfer_geom = (vectors, faces, chamfer) => {
  let chamfer_vectors = [];
  let chamfer_faces = [];
  let corner_faces = Array.from({ length: vectors.length }, () => []);
  faces.forEach(ii => {
    const fl = ii.length - 1;
    let center_point = new THREE.Vector3();
    let face = Array.from({ length: fl });
    for (let j = 0; j < fl; j++) {
      const vv = vectors[ii[j]].clone();
      center_point.add(vv);
      corner_faces[ii[j]].push((face[j] = chamfer_vectors.push(vv) - 1));
    }
    center_point.divideScalar(fl);
    for (let j = 0; j < fl; j++) {
      let vv = chamfer_vectors[face[j]];
      vv.subVectors(vv, center_point)
        .multiplyScalar(chamfer)
        .addVectors(vv, center_point);
    }
    face.push(ii[fl]);
    chamfer_faces.push(face);
  });
  for (let i = 0; i < faces.length - 1; ++i) {
    for (let j = i + 1; j < faces.length; ++j) {
      let pairs = [],
        lastm = -1;
      for (let m = 0; m < faces[i].length - 1; ++m) {
        let n = faces[j].indexOf(faces[i][m]);
        if (n >= 0 && n < faces[j].length - 1) {
          if (lastm >= 0 && m !== lastm + 1) pairs.unshift([i, m], [j, n]);
          else pairs.push([i, m], [j, n]);
          lastm = m;
        }
      }
      if (pairs.length !== 4) continue;
      chamfer_faces.push([
        chamfer_faces[pairs[0][0]][pairs[0][1]],
        chamfer_faces[pairs[1][0]][pairs[1][1]],
        chamfer_faces[pairs[3][0]][pairs[3][1]],
        chamfer_faces[pairs[2][0]][pairs[2][1]],
        -1
      ]);
    }
  }
  for (let i = 0; i < corner_faces.length; ++i) {
    let cf = corner_faces[i],
      face = [cf[0]],
      count = cf.length - 1;
    while (count) {
      for (let m = faces.length; m < chamfer_faces.length; ++m) {
        let index = chamfer_faces[m].indexOf(face[face.length - 1]);
        if (index >= 0 && index < 4) {
          if (--index === -1) index = 3;
          let next_vertex = chamfer_faces[m][index];
          if (cf.indexOf(next_vertex) >= 0) {
            face.push(next_vertex);
            break;
          }
        }
      }
      --count;
    }
    face.push(-1);
    chamfer_faces.push(face);
  }
  return { vectors: chamfer_vectors, faces: chamfer_faces };
};

export const make_geom = (vertices, faces, radius, tab, af) => {
  let geom = new THREE.Geometry();
  geom.vertices = vertices.map(v => v.multiplyScalar(radius));
  faces.forEach(ii => {
    const fl = ii.length - 1;
    const aa = (Math.PI * 2) / fl;
    for (let j = 0; j < fl - 2; j++) {
      geom.faces.push(
        new THREE.Face3(
          ii[0],
          ii[j + 1],
          ii[j + 2],
          [
            geom.vertices[ii[0]],
            geom.vertices[ii[j + 1]],
            geom.vertices[ii[j + 2]]
          ],
          0,
          ii[fl] + 1
        )
      );
      geom.faceVertexUvs[0].push([
        new THREE.Vector2(
          (Math.cos(af) + 1 + tab) / 2 / (1 + tab),
          (Math.sin(af) + 1 + tab) / 2 / (1 + tab)
        ),
        new THREE.Vector2(
          (Math.cos(aa * (j + 1) + af) + 1 + tab) / 2 / (1 + tab),
          (Math.sin(aa * (j + 1) + af) + 1 + tab) / 2 / (1 + tab)
        ),
        new THREE.Vector2(
          (Math.cos(aa * (j + 2) + af) + 1 + tab) / 2 / (1 + tab),
          (Math.sin(aa * (j + 2) + af) + 1 + tab) / 2 / (1 + tab)
        )
      ]);
    }
  });
  geom.computeFaceNormals();
  geom.boundingSphere = new THREE.Sphere(new THREE.Vector3(), radius);
  return geom;
};

export const create_geom = (vertices, faces, radius, tab, af, chamfer) => {
  const vectors = make_vectors_from_vertices(vertices);
  const cg = chamfer_geom(vectors, faces, chamfer);
  const geom = make_geom(cg.vectors, cg.faces, radius, tab, af);
  geom.cannon_shape = create_shape(vectors, faces, radius);
  return geom;
};

export const calc_texture_size = approx =>
  Math.pow(2, Math.floor(Math.log(approx) / Math.log(2)));

export const create_d4_texture = (text, color, back_color, size, margin) => {
  let canvas = createCanvas();
  let context = canvas.getContext(`2d`);
  let ts = calc_texture_size(size + margin) * 2;
  canvas.width = canvas.height = ts;
  context.font = (ts - margin) / 2.25 + "pt Arial";
  context.fillStyle = back_color;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillStyle = color;
  for (let i in text) {
    context.fillText(text[i], canvas.width / 2, canvas.height / 2 - ts * 0.3);
    context.translate(canvas.width / 2, canvas.height / 2);
    context.rotate((Math.PI * 2) / 3);
    context.translate(-canvas.width / 2, -canvas.height / 2);
  }
  let texture = new THREE.Texture(canvas);
  texture.needsUpdate = true;
  return texture;
};

export const create_text_texture = (text, color, back_color, size, margin) => {
  if (undefined === text) return null;
  let canvas = createCanvas();
  let context = canvas.getContext(`2d`);
  const ts = calc_texture_size(size + size * 2 * margin) * 2;
  canvas.width = canvas.height = ts;
  context.font = ts / (1 + 2 * margin) + `pt Arial`;
  context.fillStyle = back_color;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.textAlign = `center`;
  context.textBaseline = `middle`;
  context.fillStyle = color;
  context.fillText(text, canvas.width / 2, canvas.height / 2);
  if (text === `6` || text === `9`) {
    context.fillText(`  .`, canvas.width / 2, canvas.height / 2);
  }

  let texture = new THREE.Texture(canvas);
  texture.needsUpdate = true;
  return texture;
};

// Dice-Specific Materials
export const create_d4_materials = (
  labels,
  size,
  margin,
  textColor = default_label_color,
  diceColor = default_dice_color
) =>
  labels.map(
    label =>
      new THREE.MeshPhongMaterial(
        _.assign({}, material_options, {
          map: create_d4_texture(label, textColor, diceColor, size, margin)
        })
      )
  );

export const create_dice_materials = (
  face_labels,
  size,
  margin,
  textColor = default_label_color,
  diceColor = default_dice_color
) =>
  face_labels.map(
    label =>
      new THREE.MeshPhongMaterial(
        _.assign({}, material_options, {
          map: create_text_texture(label, textColor, diceColor, size, margin)
        })
      )
  );

export const make_random_vector = vector => {
  let { x, y } = vector;
  if (!_.isFinite(x)) x = 1;
  if (!_.isFinite(y)) y = 1;

  const random_angle = (Math.random() * Math.PI) / 5 - Math.PI / 10;
  let vec = {
    x: x * Math.cos(random_angle) - y * Math.sin(random_angle),
    y: x * Math.sin(random_angle) + y * Math.cos(random_angle)
  };
  if (vec.x === 0) vec.x = 0.01;
  if (vec.y === 0) vec.y = 0.01;
  return vec;
};

export const get_die_value = die => {
  let vector = new THREE.Vector3(0, 0, die.dieType === "d4" ? -1 : 1);
  let closest_face,
    closest_angle = Math.PI * 2;
  for (let i = 0, l = die.geometry.faces.length; i < l; ++i) {
    let face = die.geometry.faces[i];
    if (face.materialIndex === 0) continue;
    let angle = face.normal
      .clone()
      .applyQuaternion(die.body.quaternion)
      .angleTo(vector);
    if (angle < closest_angle) {
      closest_angle = angle;
      closest_face = face;
    }
  }
  let matindex = closest_face.materialIndex - 1;
  if (die.dieType === "d100") matindex *= 10;
  if (die.dieType === "d10" && matindex === 0) matindex = 10;
  return matindex;
};

export const get_dice_values = dice => dice.map(die => get_die_value(die));
